/**
 * Tohumlu rastgele sayı üretici.
 *
 * Neden kendi üreticimiz: `Math.random()` tohumlanamaz. Tohum olmadan
 * "aynı tohum aynı sayfayı üretir" vaadi tutmaz, ve o vaat olmadan
 * ne egzersizi yeniden basabiliriz ne de bir ölçümü yeniden kurabiliriz.
 *
 * sfc32 seçildi: hızlı, 32-bit tamsayı aritmetiği (kayan nokta sapması yok),
 * istatistiksel kalitesi bu iş için fazlasıyla yeterli. Kriptografik DEĞİL —
 * burada gizlilik değil tekrarlanabilirlik arıyoruz.
 */

/** Tek 32-bit tohumdan dört duruma dağıtır (splitmix32) */
function splitmix32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x9e3779b9) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return (t ^ (t >>> 14)) >>> 0
  }
}

export class Rng {
  private a = 0
  private b = 0
  private c = 0
  private d = 0

  /** Tohum bigint olabilir: veritabanında seed bigint olarak saklanıyor */
  constructor(seed: number | bigint) {
    const s =
      typeof seed === 'bigint'
        ? Number(BigInt.asUintN(32, seed)) ^ Number(BigInt.asUintN(32, seed >> 32n))
        : seed >>> 0
    const next = splitmix32(s)
    this.a = next()
    this.b = next()
    this.c = next()
    this.d = next()
    // İlk çıktıların tohuma fazla benzememesi için ısıtma
    for (let i = 0; i < 12; i++) this.uint32()
  }

  uint32(): number {
    const t = (this.a + this.b) >>> 0
    this.a = this.b ^ (this.b >>> 9)
    this.b = (this.c + (this.c << 3)) >>> 0
    this.c = ((this.c << 21) | (this.c >>> 11)) >>> 0
    this.d = (this.d + 1) >>> 0
    const r = (t + this.d) >>> 0
    this.c = (this.c + r) >>> 0
    return r
  }

  /** [0, 1) */
  float(): number {
    return this.uint32() / 0x100000000
  }

  /** [min, max] tamsayı, her iki uç dahil */
  int(min: number, max: number): number {
    if (max < min) throw new Error(`Rng.int: max < min (${min}, ${max})`)
    return min + Math.floor(this.float() * (max - min + 1))
  }

  bool(pTrue = 0.5): boolean {
    return this.float() < pTrue
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick: boş dizi')
    return items[this.int(0, items.length - 1)]!
  }

  /** Yerinde değil, kopya döndürür (Fisher-Yates) */
  shuffle<T>(items: readonly T[]): T[] {
    const out = items.slice()
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(0, i)
      const tmp = out[i]!
      out[i] = out[j]!
      out[j] = tmp
    }
    return out
  }

  /**
   * n farklı öğe seç. Tekrarsız örnekleme; n > havuz ise hata.
   */
  sample<T>(items: readonly T[], n: number): T[] {
    if (n > items.length) throw new Error(`Rng.sample: ${n} > ${items.length}`)
    return this.shuffle(items).slice(0, n)
  }

  /**
   * Belirli bir alt akış. Aynı ana tohumdan görev başına bağımsız akış
   * üretmek için: bir görevin deneme sayısı değişse bile diğer görevlerin
   * uyarıcıları kaymaz. Bu, paralel form üretiminde kritik.
   */
  stream(label: string): Rng {
    let h = 2166136261
    for (let i = 0; i < label.length; i++) {
      h ^= label.charCodeAt(i)
      h = Math.imul(h, 16777619)
    }
    return new Rng((this.uint32() ^ h) >>> 0)
  }
}

/** Tohumu metinden türet — form kodu, danışan, hafta gibi şeylerden */
export function seedFrom(...parts: (string | number)[]): number {
  let h = 2166136261
  const s = parts.join('|')
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}
